import React, { useState, useEffect, useRef } from 'react';
import { playWord } from '../utils/audio';
import { API_BASE } from '../api';
import { useSwipe } from '../hooks/useSwipe';
import { STATIC } from '../staticMode';
import { lookupGloss, lookupGlossFull } from '../dict';

const API = API_BASE;

// Word popup: meaning + learning status + preview. Timing is edited on the
// waveform now, so the numeric nudge editor is gone.
export default function WordPopup({
  word, dialect, sentenceId, wordIdx, allWords, audioUrl, audioStart,
  status, onStatusChange, onClose, onJumpToReview,
  getGloss, setGloss, getTimes,
}) {
  const [omniGloss, setOmniGloss]       = useState(null);
  const [omniEn, setOmniEn]             = useState(null);   // English line (static Truku Bible)
  const [editingGloss, setEditingGloss] = useState(false);
  const [glossVal, setGlossVal]         = useState('');

  const ref      = useRef();
  const glossRef = useRef();
  const userGloss = getGloss(word.text);

  useEffect(() => {
    setOmniGloss(null); setOmniEn(null); setEditingGloss(false);
    if (STATIC) {                                    // bundled dict, no backend
      const g = lookupGlossFull(word.text, dialect);
      setOmniGloss(g.zh || ''); setOmniEn(g.en || '');
      return;
    }
    const dq = dialect ? `?dialect=${encodeURIComponent(dialect)}` : '';
    fetch(`${API}/dictionary/${encodeURIComponent(word.text)}${dq}`)
      .then(r => r.json()).then(d => setOmniGloss(d.gloss || '')).catch(() => setOmniGloss(''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.text, sentenceId, wordIdx, dialect]);

  useEffect(() => {
    if (editingGloss && glossRef.current) glossRef.current.focus();
  }, [editingGloss]);

  useEffect(() => {
    function onMouse(e) {
      // keep the popup open while editing the word's block on the waveform
      if (e.target.closest && e.target.closest('.wave')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [onClose]);

  function playCurrentTimes() {
    if (!audioUrl) return;
    const t = getTimes ? getTimes(sentenceId, wordIdx, word) : word;
    playWord(audioUrl, (audioStart ?? 0) + t.start, (audioStart ?? 0) + t.end);
  }

  // Keyboard: ← → prev/next word, space preview, Esc close
  const playRef     = useRef(playCurrentTimes);
  const onCloseRef  = useRef(onClose);
  const allWordsRef = useRef(allWords);
  const wordIdxRef  = useRef(wordIdx);
  playRef.current     = playCurrentTimes;
  onCloseRef.current  = onClose;
  allWordsRef.current = allWords;
  wordIdxRef.current  = wordIdx;

  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault(); e.stopPropagation();
        const p = allWordsRef.current[wordIdxRef.current - 1];
        if (p) onCloseRef.current(p, wordIdxRef.current - 1);
        else onCloseRef.current('__cross__', -1);     // first word → previous segment's last word
      } else if (e.key === 'ArrowRight') {
        e.preventDefault(); e.stopPropagation();
        const n = allWordsRef.current[wordIdxRef.current + 1];
        if (n) onCloseRef.current(n, wordIdxRef.current + 1);
        else onCloseRef.current('__cross__', 1);      // last word → next segment's first word
      } else if (e.key === ' ') {
        e.preventDefault(); e.stopPropagation();
        playRef.current();
      } else if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function startEditGloss() { setGlossVal(userGloss ?? omniGloss ?? ''); setEditingGloss(true); }
  function saveGloss() { setGloss(word.text, glossVal.trim()); setEditingGloss(false); }

  const displayGloss = userGloss ?? omniGloss;
  const prevWord = allWords[wordIdx - 1];
  const nextWord = allWords[wordIdx + 1];

  // Swipe: left = next word, right = prev word (mirrors the ← → keys above).
  const swipe = useSwipe({
    onLeft:  () => { nextWord ? onClose(nextWord, wordIdx + 1) : onClose('__cross__', 1); },
    onRight: () => { prevWord ? onClose(prevWord, wordIdx - 1) : onClose('__cross__', -1); },
  });

  return (
    <div className="word-popup" ref={ref} {...swipe}>
      {/* 跳到複習 — above the word; learning words only (caller decides which views). */}
      {status === 'learning' && onJumpToReview && (
        <button tabIndex={-1} className="popup-jump-btn" onClick={() => onJumpToReview()}>跳到複習 ↗</button>
      )}
      <div className="popup-header">
        <span className="popup-word">{word.text}</span>
        <div className="popup-header-actions">
          {audioUrl && (
            <button tabIndex={-1} className="popup-play-btn" onClick={playCurrentTimes}>▶</button>
          )}
          <button tabIndex={-1} className="popup-close" onClick={() => onClose()}>×</button>
        </div>
      </div>

      <div className="popup-body">
        {omniGloss === null && !userGloss && <div className="popup-loading">...</div>}
        {STATIC ? (
          // Read-only reader: display the bundled trilingual gloss, no editing.
          <div className="popup-gloss-row">
            {displayGloss || omniEn
              ? <>
                  {displayGloss && <span className="popup-gloss">{displayGloss}</span>}
                  {omniEn && <div className="popup-gloss-en">{omniEn}</div>}
                </>
              : <span className="popup-no-def">（查無此詞）</span>}
          </div>
        ) : editingGloss ? (
          <div className="popup-edit-row">
            <input ref={glossRef} className="popup-edit-input" value={glossVal}
              onChange={e => setGlossVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveGloss(); if (e.key === 'Escape') setEditingGloss(false); }}
              placeholder="輸入中文意思" />
            <button tabIndex={-1} className="popup-save-btn" onClick={saveGloss}>儲存</button>
          </div>
        ) : (
          <div className="popup-gloss-row" onClick={startEditGloss} title="點擊修改">
            {displayGloss
              ? <span className={`popup-gloss${userGloss ? ' user-edited' : ''}`}>{displayGloss}</span>
              : <span className="popup-no-def">（查無此詞）</span>}
            <span className="popup-edit-icon">✎ 修改{userGloss ? ' ·已修改' : ''}</span>
          </div>
        )}
      </div>

      <div className="popup-status">
        <button tabIndex={-1} className={`status-btn btn-new${status === 'new' ? ' active' : ''}`}
          onClick={() => onStatusChange('new')}>未學</button>
        <button tabIndex={-1} className={`status-btn btn-learning${status === 'learning' ? ' active' : ''}`}
          onClick={() => onStatusChange('learning')}>學習中</button>
        <button tabIndex={-1} className={`status-btn btn-known${status === 'known' ? ' active' : ''}`}
          onClick={() => onStatusChange('known')}>已知</button>
      </div>

      {/* Word timing is edited on the waveform (drag the cuts), not here. */}
    </div>
  );
}
